import React, { ChangeEvent, Dispatch, SetStateAction } from 'react'
import { Switch } from '../../components/ui/switch'

import { SettingItem, SettingsCard } from '../../components/settings/SettingsCard'
import { useSmartBidSettings } from './DefaultSmartBidSettings'
import { isValidNumericInput } from '../../util/numeric_input'
import { Input } from '../../components/inputs/Input'

interface DefaultSmartBidPlusSettingsProps {
    selectedProfile: Models.Profile
}

export const DefaultSmartBidPlusSettings = ({
    selectedProfile,
}: DefaultSmartBidPlusSettingsProps) => {
    const {
        loading,
        saveBidsSettings,
        smartBidDefault,
        dirty,
        setDirty,
        thresholdTargetACOS,
        setThresholdTargetACOS,
        maximumBidAdjustment,
        setMaximumBidAdjustment,
        lookbackDays,
        setLookbackDays,
        saveAccountSettings,
    } = useSmartBidSettings(selectedProfile)

    const enableSmartBidsPlus = (
        <Switch
            checked={smartBidDefault}
            large
            onChange={(e: ChangeEvent<any>) => {
                saveAccountSettings({ smart_bid_default: e.target.checked })
            }}
        />
    )

    const set =
        (set: Dispatch<SetStateAction<string | number | null>>, allowDecimals: boolean = true) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            if (!isValidNumericInput(value, allowDecimals)) {
                return
            }

            set(value)
            setDirty(true)
        }

    return (
        <SettingsCard
            title={'Default Smart Bid+ Settings'}
            onSave={dirty ? saveBidsSettings : null}
            loading={loading}
            description="Smart Bids+ automatically changes bids on a daily basis for campaigns
        with Smart Bids+ enabled. Bid changes are only applied to targets or keywords with
        two or more orders within the look back period"
        >
            <SettingItem
                title="Enabled by Default"
                description="When activated, Smart Bids+ will automatically be turned on for
            any new campaigns synced with Merch Jar."
                rightComponent={<div className="mt-2">{enableSmartBidsPlus}</div>}
            />
            <SettingItem
                title="Look Back Period"
                description="Indicates the historical data range used by the Smart Bid+
            algorithm, ignoring today and yesterday's data."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={lookbackDays as number}
                        onChange={set(setLookbackDays, false)}
                        suffix="days"
                    />
                }
            />
            <SettingItem
                title="Target ACOS Threshold"
                description="Buffer that ensures your bids remain stable when your actual
            ACOS is close to your target. For instance, with a 25% target and a 2%
            threshold, bids won't change if the ACOS lies between 23% and 27%."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={thresholdTargetACOS as number}
                        onChange={set(setThresholdTargetACOS)}
                        suffix="%"
                    />
                }
            />
            <SettingItem
                title="Max Bid Adjustment"
                description="Maximum percentage change made to your bids each day."
                rightComponent={
                    <Input
                        style={{ width: '100px' }}
                        value={maximumBidAdjustment as number}
                        onChange={set(setMaximumBidAdjustment)}
                        suffix="%"
                    />
                }
                bottomDivider={false}
            />
        </SettingsCard>
    )
}
